import { HttpError } from '../../sagas/Clients/HttpError';
import {
  Account,
  Organization,
} from '../../sagas/Clients/AccountClient/Models/Account';
import { User } from '../../models/User';

export enum Type {
  FETCH_ACCOUNT_REQUEST = 'ACCOUNT/FETCH_ACCOUNT_REQUEST',
  FETCH_ACCOUNT_SUCCESS = 'ACCOUNT/FETCH_ACCOUNT_SUCCESS',
  FETCH_ACCOUNT_FAILURE = 'ACCOUNT/FETCH_ACCOUNT_FAILURE',
  DISABLE_2FA_REQUEST = 'ACCOUNT/DISABLE_2FA_REQUEST',
  DISABLE_2FA_SUCCESS = 'ACCOUNT/DISABLE_2FA_SUCCESS',
  DISABLE_2FA_FAILURE = 'ACCOUNT/DISABLE_2FA_FAILURE',
  ENABLE_2FA_REQUEST = 'ACCOUNT/ENABLE_2FA_REQUEST',
  GENERATE_2FA_REQUEST = 'ACCOUNT/GENERATE_2FA_REQUEST',
  PASSWORD_UPDATE_REQUEST = 'ACCOUNT/PASSWORD_UPDATE_REQUEST',
  PHOTO_UPDATE_REQUEST = 'ACCOUNT/PHOTO_UPDATE_REQUEST',
  PROFILE_UPDATE_REQUEST = 'ACCOUNT/PROFILE_UPDATE_REQUEST',
  PROFILE_UPDATE_SUCCESS = 'ACCOUNT/PROFILE_UPDATE_SUCCESS',
  PROFILE_UPDATE_FAILURE = 'ACCOUNT/PROFILE_UPDATE_FAILURE',
  RESEND_EMAIL_REQUEST = 'ACCOUNT/RESEND_EMAIL_REQUEST',
  RESEND_EMAIL_SUCCESS = 'ACCOUNT/RESEND_EMAIL_SUCCESS',
  RESEND_EMAIL_FAILURE = 'ACCOUNT/RESEND_EMAIL_FAILURE',
  STORAGE_UPDATE_REQUEST = 'ACCOUNT/STORAGE_UPDATE_REQUEST',
  STORAGE_UPDATE_SUCCESS = 'ACCOUNT/STORAGE_UPDATE_SUCCESS',
  STORAGE_UPDATE_FAILURE = 'ACCOUNT/STORAGE_UPDATE_FAILURE',
  STORAGE_UPDATE_KEY = 'ACCOUNT/STORAGE_UPDATE_KEY',
  DISPLAY_NAME_REQUEST = 'ACCOUNT/DISPLAY_NAME_REQUEST',
  DISPLAY_NAME_SUCCESS = 'ACCOUNT/DISPLAY_NAME_SUCCESS',
  DISPLAY_NAME_FAILURE = 'ACCOUNT/DISPLAY_NAME_FAILURE',
}

interface Action<T> {
  type: T;
}

export interface FetchAccountRequest
  extends Action<Type.FETCH_ACCOUNT_REQUEST> {}

export interface FetchAccountSuccess
  extends Action<Type.FETCH_ACCOUNT_SUCCESS> {
  data?: Account;
}

export interface FetchAccountFailure
  extends Action<Type.FETCH_ACCOUNT_FAILURE> {
  error?: string | HttpError;
}

export interface ResendEmailRequest extends Action<Type.RESEND_EMAIL_REQUEST> {}

export interface ResendEmailSuccess extends Action<Type.RESEND_EMAIL_SUCCESS> {}

export interface ResendEmailFailure extends Action<Type.RESEND_EMAIL_FAILURE> {
  error?: string | HttpError;
}

export interface UpdateUserStorageRequest
  extends Action<Type.STORAGE_UPDATE_REQUEST> {
  keyValuePair: {
    key: string;
    value: string;
  };
}

export interface UpdateUserStorageSuccess
  extends Action<Type.STORAGE_UPDATE_SUCCESS> {
  key: string;
  value: string;
}

export interface UpdateUserStorageFailure
  extends Action<Type.STORAGE_UPDATE_FAILURE> {
  error?: string | HttpError;
}

export interface PhotoUpdateRequest extends Action<Type.PHOTO_UPDATE_REQUEST> {
  image: File;
  callback: (success: boolean) => void;
}

export interface ProfileUpdateRequest
  extends Action<Type.PROFILE_UPDATE_REQUEST> {
  name?: string;
  company?: string;
  company_url?: string;
  terms_accepted?: boolean;
  callback?: () => void;
}

export interface ProfileUpdateSuccess
  extends Action<Type.PROFILE_UPDATE_SUCCESS> {}

export interface ProfileUpdateFailure
  extends Action<Type.PROFILE_UPDATE_FAILURE> {
  error: string | HttpError;
}

export interface DisplayNameRequest extends Action<Type.DISPLAY_NAME_REQUEST> {
  display_name: string;
}

export interface DisplayNameSuccess extends Action<Type.DISPLAY_NAME_SUCCESS> {}

export interface DisplayNameFailure extends Action<Type.DISPLAY_NAME_FAILURE> {
  error: string | HttpError;
}

export interface Disable2FARequest extends Action<Type.DISABLE_2FA_REQUEST> {
  confirmationPassword: string;
  callback: () => void;
}

export interface Disable2FASuccess extends Action<Type.DISABLE_2FA_SUCCESS> {}

export interface Disable2FAFailure extends Action<Type.DISABLE_2FA_FAILURE> {
  error: string | HttpError;
}

export interface Enable2FARequest extends Action<Type.ENABLE_2FA_REQUEST> {
  code: string;
  callback: (recoveryCode: string, error: string) => void;
}

export interface Generate2FARequest extends Action<Type.GENERATE_2FA_REQUEST> {
  callback: (codeUrl: string, error: string) => void;
}

export interface UpdatePasswordRequest
  extends Action<Type.PASSWORD_UPDATE_REQUEST> {
  oldPassword: string;
  newPassword: string;
  callback: (success: boolean, error: string) => void;
}

type Actions =
  | FetchAccountRequest
  | FetchAccountSuccess
  | FetchAccountFailure
  | ResendEmailRequest
  | ResendEmailSuccess
  | ResendEmailFailure
  | UpdateUserStorageRequest
  | UpdateUserStorageSuccess
  | UpdateUserStorageFailure
  | PhotoUpdateRequest
  | ProfileUpdateRequest
  | ProfileUpdateSuccess
  | ProfileUpdateFailure
  | DisplayNameRequest
  | DisplayNameSuccess
  | DisplayNameFailure
  | Disable2FARequest
  | Disable2FASuccess
  | Disable2FAFailure
  | Enable2FARequest
  | Generate2FARequest
  | UpdatePasswordRequest;

export interface State {
  isLoading: boolean;
  isUpdating: boolean;
  error?: string | HttpError;
  organizations: Organization[];
  user: User | null;
  userStorage: UserStorage | null;
}

export interface UserStorage {
  [key: string]: string;
}

export const initialState: State = {
  isLoading: false,
  isUpdating: false,
  error: undefined,
  organizations: [],
  user: null,
  userStorage: null,
};

export default (state = initialState, action: Actions): State => {
  switch (action.type) {
    case Type.FETCH_ACCOUNT_REQUEST:
      return {
        ...state,
        isLoading: true,
        error: undefined,
      };
    case Type.FETCH_ACCOUNT_SUCCESS:
      return {
        ...state,
        isLoading: false,
        organizations: action.data?.organizations ?? [],
        user: action.data?.user ?? null,
        userStorage: action.data?.userStorage ?? null,
        error: undefined,
      };
    case Type.FETCH_ACCOUNT_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.error,
      };
    case Type.PROFILE_UPDATE_REQUEST:
      return { ...state, isUpdating: true, error: undefined };
    case Type.PROFILE_UPDATE_SUCCESS:
      return { ...state, isUpdating: false };
    case Type.PROFILE_UPDATE_FAILURE:
      return { ...state, isUpdating: false, error: action.error };
    case Type.RESEND_EMAIL_REQUEST:
      return { ...state, isUpdating: true, error: undefined };
    case Type.RESEND_EMAIL_SUCCESS:
      return { ...state, isUpdating: false };
    case Type.RESEND_EMAIL_FAILURE:
      return { ...state, isUpdating: false, error: action.error };
    case Type.STORAGE_UPDATE_REQUEST:
      return { ...state, isUpdating: true, error: undefined };
    case Type.STORAGE_UPDATE_SUCCESS:
      if (!state.userStorage) {
        return state;
      }
      return {
        ...state,
        isUpdating: false,
        userStorage: { ...state.userStorage, [action.key]: action.value },
      };
    case Type.STORAGE_UPDATE_FAILURE:
      return { ...state, isUpdating: false, error: action.error };
    case Type.DISABLE_2FA_REQUEST:
      return { ...state, isUpdating: true, error: undefined };
    case Type.DISABLE_2FA_SUCCESS:
      return { ...state, isUpdating: false };
    case Type.DISABLE_2FA_FAILURE:
      return { ...state, isUpdating: false, error: action.error };
    case Type.DISPLAY_NAME_REQUEST:
      return { ...state, isUpdating: true, error: undefined };
    case Type.DISPLAY_NAME_SUCCESS:
      return { ...state, isUpdating: false };
    case Type.DISPLAY_NAME_FAILURE:
      return { ...state, isUpdating: false, error: action.error };
    default:
      return state;
  }
};

export const actions = {
  fetchAccount: (): FetchAccountRequest => ({
    type: Type.FETCH_ACCOUNT_REQUEST,
  }),
  setAccount: (data?: Account): FetchAccountSuccess => ({
    type: Type.FETCH_ACCOUNT_SUCCESS,
    data,
  }),
  setAccountError: (error?: string | HttpError): FetchAccountFailure => ({
    type: Type.FETCH_ACCOUNT_FAILURE,
    error,
  }),
  resendEmail: (): ResendEmailRequest => ({ type: Type.RESEND_EMAIL_REQUEST }),
  updateUserStorage: (keyValuePair: {
    key: string;
    value: string;
  }): UpdateUserStorageRequest => ({
    type: Type.STORAGE_UPDATE_REQUEST,
    keyValuePair,
  }),
  updatePhoto: (
    image: File,
    callback: (success: boolean) => void
  ): PhotoUpdateRequest => ({
    type: Type.PHOTO_UPDATE_REQUEST,
    image,
    callback,
  }),
  updateProfile: ({
    ...values
  }: {
    name?: string;
    company?: string;
    company_url?: string;
    terms_accepted?: boolean;
    callback?: () => void;
  }): ProfileUpdateRequest => ({
    type: Type.PROFILE_UPDATE_REQUEST,
    ...values,
  }),
  requestDisplayNameChange: (display_name: string): DisplayNameRequest => ({
    type: Type.DISPLAY_NAME_REQUEST,
    display_name,
  }),
  disable2fa: (
    confirmationPassword: string,
    callback: () => void
  ): Disable2FARequest => ({
    type: Type.DISABLE_2FA_REQUEST,
    confirmationPassword,
    callback,
  }),
  enable2fa: (
    code: string,
    callback: (recoveryCode: string, error: string) => void
  ): Enable2FARequest => ({
    type: Type.ENABLE_2FA_REQUEST,
    code,
    callback,
  }),
  generate2faCode: (
    callback: (codeUrl: string, error: string) => void
  ): Generate2FARequest => ({
    type: Type.GENERATE_2FA_REQUEST,
    callback,
  }),
  updatePassword: (
    oldPassword: string,
    newPassword: string,
    callback: (success: boolean, error: string) => void
  ): UpdatePasswordRequest => ({
    type: Type.PASSWORD_UPDATE_REQUEST,
    oldPassword,
    newPassword,
    callback,
  }),
};
